import { ReactElement } from 'react';
import { Divider, ListSubheader, Select } from '@mui/material';

import IconChip from '../IconChip';

import { ChipSelectContainer, Error, PlaceholderText, StyledMenuItem } from './ChipSelect.styles';

export interface ChipSelectOption {
  label: string;
  value: string;
  group?: string;
  icon?: ReactElement;
}

export interface ChipSelectProps {
  options: ChipSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
}

const ChipSelect = ({ options, value, onChange, error, helperText, placeholder }: ChipSelectProps): ReactElement => {
  const hasGroups = options.some((option) => option.group);

  let menuItems;

  if (hasGroups) {
    // Grouped rendering
    const groupedOptions = options.reduce<Record<string, ChipSelectOption[]>>((acc, option) => {
      const group = option.group || 'Other';

      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(option);

      return acc;
    }, {});

    const groupEntries = Object.entries(groupedOptions);

    menuItems = groupEntries.flatMap(([group, groupOptions], idx) => {
      const isLast = idx === groupEntries.length - 1;

      return [
        <ListSubheader key={group}>{group}</ListSubheader>,
        ...groupOptions.map((option) => (
          <StyledMenuItem key={option.value} value={option.value}>
            <IconChip value={option.value} label={option.label} />
          </StyledMenuItem>
        )),
        !isLast && <Divider sx={{ m: '0 .5rem', borderBottomWidth: 'medium' }} key={`divider-${group}`} />,
      ].filter(Boolean);
    });
  } else {
    // Flat rendering (backwards compatible)
    menuItems = options.map((option) => (
      <StyledMenuItem key={option.value} value={option.value}>
        <IconChip value={option.value} label={option.label} />
      </StyledMenuItem>
    ));
  }

  return (
    <ChipSelectContainer>
      <Select
        error={error}
        fullWidth
        label="label"
        displayEmpty
        MenuProps={{
          BackdropProps: {
            style: {
              opacity: 0,
            },
          },
        }}
        labelId="select-with-chips-label"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) => {
          if (!selected) {
            return <PlaceholderText>{placeholder}</PlaceholderText>;
          }
          const selectedOption = options.find((option) => option.value === selected);

          return selectedOption ? <IconChip value={selectedOption.value} label={selectedOption.label} /> : null;
        }}
      >
        {menuItems}
      </Select>
      {helperText && (
        <Error variant="body2" className={error ? 'error' : ''}>
          {helperText}
        </Error>
      )}
    </ChipSelectContainer>
  );
};

export default ChipSelect;
